// todo add react query

import { fetchAPI } from '@quickbit/backoffice-core-frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const queryKeys = {
  base: () => ['triggeredRules.base'] as const,
  triggeredRules: () =>
    [...queryKeys.base(), 'triggeredRules.triggered-rules'] as const,
  triggeredRulesCSV: () =>
    [...queryKeys.base(), 'triggeredRules.triggered-rules-csv'] as const,
  dryRunRule: (ruleName: string) =>
    [...queryKeys.base(), 'triggeredRules.dry-run-rule', ruleName] as const,
};

export interface RuleViolations {
  userId: string;
  createdAt: Date;
}

export interface RulesOverview {
  name: string;
  total: number;
  uniqueUsers: number;
  violations: RuleViolations[];
  tags?: string[];
  description?: string;
  isEnabled?: boolean;
}

interface TriggeredRulesResponse {
  overviews: RulesOverview[];
}

interface DryRunRuleResponse {
  description: string;
}

interface ToggleRuleRequest {
  ruleName: string;
  isEnabled: boolean;
}

interface ToggleRuleResponse {
  ruleName: string;
  isEnabled: boolean;
}

export interface TriggeredRulesFilters {
  dateFilterRange: [Date | null, Date | null];
  loginCountry?: string | null;
  selectedRuleName: string | null;
}

export const useTriggeredRulesApi = () => {
  const useDryRunRule = (ruleName: string) => {
    const url = `v1/rules-engine/dry-run?rule_name=${ruleName}`;

    return useQuery({
      enabled: false,
      queryKey: [...queryKeys.dryRunRule(ruleName)],
      queryFn: async () =>
        (await fetchAPI<DryRunRuleResponse>(url)).description,
    });
  };

  const useGetTriggeredRules = (filters: TriggeredRulesFilters) => {
    let url = `v1/rules-engine/triggered-rules?`;

    if (filters.dateFilterRange[0]) {
      url += `&created_from=${filters.dateFilterRange[0].toISOString()}`;
    }
    if (filters.dateFilterRange[1]) {
      url += `&created_to=${filters.dateFilterRange[1].toISOString()}`;
    }
    if (filters.loginCountry) {
      url += `&login_country=${filters.loginCountry}`;
    }

    return useQuery({
      initialData: [],
      queryKey: [...queryKeys.triggeredRules(), filters],
      queryFn: async () =>
        (await fetchAPI<TriggeredRulesResponse>(url)).overviews,
    });
  };

  const getCSV = async (filters: TriggeredRulesFilters) => {
    let url = `v1/rules-engine/triggered-rules:csv?`;

    if (filters.dateFilterRange[0]) {
      url += `&created_from=${filters.dateFilterRange[0].toISOString()}`;
    }
    if (filters.dateFilterRange[1]) {
      url += `&created_to=${filters.dateFilterRange[1].toISOString()}`;
    }
    if (filters.loginCountry) {
      url += `&login_country=${filters.loginCountry}`;
    }
    if (filters.selectedRuleName) {
      url += `&rule_name=${filters.selectedRuleName}`;
    }

    const response = await fetchAPI(url, {
      method: 'GET',
      stream: true,
    });
    const safeChunks = response.map((c) => new Uint8Array(c));
    const blob = new Blob(safeChunks, { type: 'text/csv' });
    return blob;
  };

  const useToggleRule = () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async ({ ruleName, isEnabled }: ToggleRuleRequest) => {
        const url = `v1/rules-engine/toggle-rule`;
        return await fetchAPI<ToggleRuleResponse>(url, {
          method: 'PATCH',
          body: {
            ruleName,
            isEnabled,
          },
        });
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.triggeredRules(),
        });
      },
    });
  };

  return {
    useDryRunRule,
    useGetTriggeredRules,
    getCSV,
    useToggleRule,
  };
};
